import { z } from "zod";
import {
    GoogleSheetsScheduleCadenceSchema,
    GoogleSheetsScheduleManagedReportSchema,
    GoogleSheetsScheduleMetadataSchema,
    GoogleSheetsScheduleSchema,
} from "./google-sheets-schedule.schema";

const SCRIPT_USERCALLBACK_PATTERN =
    /^https:\/\/script\.google\.com\/macros\/d\/[^/]+\/usercallback$/;
const OAuthStateSchema = z.string().regex(/^[A-Za-z0-9]{32,128}$/);
const OAuthCodeSchema = z.string().min(1).max(2048);

export const CreateConnectionBodySchema = z.object({
    code: z.string().min(1),
    redirectUri: z
        .string()
        .url()
        .regex(
            SCRIPT_USERCALLBACK_PATTERN,
            "redirectUri must be a Google Apps Script usercallback URL",
        ),
});

export const StartConnectionResponseSchema = z.object({
    state: OAuthStateSchema,
    redirectUri: z.string().url(),
});

export const CompleteConnectionBodySchema = z.object({
    state: OAuthStateSchema,
    code: OAuthCodeSchema,
});

export const OAuthCallbackQuerySchema = z.object({
    state: OAuthStateSchema,
    code: OAuthCodeSchema.optional(),
    error: z.string().min(1).max(256).optional(),
});

export const OAuthResultBodySchema = z.object({
    state: OAuthStateSchema,
});

export const OAuthResultSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("pending") }),
    z.object({ status: z.literal("completed"), code: z.string() }),
    z.object({ status: z.literal("failed"), error: z.string() }),
]);

export const SchedulePathParamsSchema = z.object({
    id: z.string().uuid(),
});

export const SpreadsheetPathParamsSchema = z.object({
    spreadsheetId: z.string().min(1),
});

export const CreateScheduleBodySchema = z.object({
    spreadsheetId: z.string().min(1),
    spreadsheetTitle: z.string().nullable().optional(),
    cadence: GoogleSheetsScheduleCadenceSchema,
    managedReports: z
        .array(GoogleSheetsScheduleManagedReportSchema)
        .min(1)
        .max(50),
    metadata: GoogleSheetsScheduleMetadataSchema.optional(),
});

export const ConnectionResponseSchema = z.object({
    isConnected: z.boolean(),
});

export const ReportAccessResponseSchema = z.object({
    isSchedulesAvailable: z.boolean(),
});

export const ConnectionReuseResponseSchema = z.object({
    reused: z.boolean(),
});

export const ScheduleResponseSchema = z.object({
    schedule: GoogleSheetsScheduleSchema,
});

export const RefreshNowResponseSchema = z.object({
    accepted: z.literal(true),
});

export const PickerCredentialsResponseSchema = z.object({
    accessToken: z.string().min(1),
    apiKey: z.string().min(1),
    appId: z.string().min(1),
});

export const SpreadsheetAccessResponseSchema = z.object({
    hasAccess: z.boolean(),
});

export type ConnectionResponse = z.infer<typeof ConnectionResponseSchema>;
export type CreateConnectionBody = z.infer<typeof CreateConnectionBodySchema>;
export type CreateScheduleBody = z.infer<typeof CreateScheduleBodySchema>;
export type OAuthResult = z.infer<typeof OAuthResultSchema>;
